import React, { useEffect, useState } from "react";
import Loader from "../loader/loader";
import { ImgTopBtn, SmallPlainInsetBtnEventListener } from "../buttons";
import BlinkingLight from "../blinkingLight/blinkingLight";
import { FORM_FLOW_CONTROL } from "../../constants/currentContextTypes";
import { CellPhoneIcon } from "../icons";
import ErrorMessageModal from "../errorMessageModal/errorMessageModal";
import { getStorageKey } from "../../utils/localStorageStateUniqueKeyGenerator";
import "./verify-modal.scss";

const OtpPhoneVerificationForm = ({
  onRequestResend,
  userId,
  onCancel,
  onMobileVerification,
  onSkipVerification,
  currentContextType,
}) => {
  const [codeValue, setCodeValue] = useState("");
  const [phoneVerifyFocus, setPhoneVerifyFocus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(false);
  const [time, setTime] = useState(30);
  const referralValue = localStorage.getItem(
    getStorageKey("usernameReferral", currentContextType)
  );
  // **Pure React state** for the resend count
  const [resendCount, setResendCount] = useState(0);
  const skipCondition =
    !(
      currentContextType === FORM_FLOW_CONTROL.RESET_PASSWORD_FORM ||
      currentContextType === FORM_FLOW_CONTROL.SECURITY_CHECK ||
      currentContextType === FORM_FLOW_CONTROL.MEMBER_SOURCE_SKIT_UPLOAD_PERMISSIONS ||
      currentContextType === FORM_FLOW_CONTROL.MEMBER_VERIFICATION_NEEDED
    ) &&
    !referralValue &&
    resendCount > 4 &&
    time === 0;

  const isButtonDisabled = time !== 0;
  const [otpError, setOtpError] = useState("");

  // Countdown timer
  useEffect(() => {
    if (time === 0) return;
    const id = setTimeout(() => setTime((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [time]);

  // Enable “Confirm” once 6 digits are entered
  useEffect(() => {
    setActive(codeValue.length === 6);
  }, [codeValue]);

  // Mount/unmount logger (for your curiosity)
  useEffect(() => {
    console.log("🏗️ OTP form mounted");
    return () => console.log("🗑️ OTP form unmounted");
  }, []);

  const handleRequestNewCode = async () => {
    if (time > 0) return;

    setTime(30);
    setLoading(true);
    try {
      await onRequestResend();
      setResendCount((c) => c + 1);
      console.log("[CHILD] New resendCount:", resendCount + 1);
    } catch (err) {
      console.error("[CHILD] onRequestResend error:", err);
    } finally {
      setLoading(false);
    }
  };
  const handleCodeChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCodeValue(digits);
  };
  
  // allow Enter to submit (optional)
  const handleCodeKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleConfirm(e);
    }
  };
  const handleConfirm = async (e) => {
    e.preventDefault();
    setOtpError("");
    const digits = codeValue.replace(/\D/g, "");
    // ✅ show your modal if empty or incomplete
    if (digits.length === 0) {
      setOtpError("Please enter the code sent to your phone.");
      return;
    }
    if (digits.length < 6) {
      setOtpError("Please enter the full 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      console.log("[CHILD] 🔍 handleConfirm (VERIFY) with", { userId, digits });
      await onMobileVerification(userId, digits);
      console.log("[CHILD] ✅ OTP verify succeeded, returning to UI");
    } catch (err) {
      console.error("[CHILD] OTP verify failed:", err);
      setOtpError(err.message || "Verification error");
    } finally {
      setLoading(false);
    }
  };
  const handleCancel = (e) => {
    // hand your setLoading into the parent handler
    onCancel(e, setLoading);
  };

  return (
    <div className="verify-modal-content slideDown">
      <div className="verify-modal-body">
        <form action="" className="verify-modal-form">
          {/* <!-- start --> */}
          <div className="verification-container">
            <div className="header-grid blinker-wrapper">
              <div className="verify-modal-header">
                {/* <!-- <div class="img-div"></div> --> */}
                <CellPhoneIcon />
                <h1>Verify Mobile</h1>
              </div>
              <p
                className={`verify-modal-timer ${time === 0 ? "request-code-allowed " : ""}`}
                id="timer"
              >
                {time} Seconds
              </p>
              <p className="verify-modal-timer-text">
                Must wait 30 seconds to request a new code
              </p>
              <p className="responsiveBtnText">Enter Code Sent to Mobile* </p>
              <input
                id="verify-input"
                type="text"
                minLength="6"
                maxLength="6"
                placeholder="# # # # # #"
                value={codeValue}
                onChange={handleCodeChange}     // ← CHANGED
                onKeyDown={handleCodeKeyDown}   // ← NEW (optional)
                onFocus={() => setPhoneVerifyFocus(true)}
                onBlur={() => setPhoneVerifyFocus(false)}
                className={`${phoneVerifyFocus ? "focused" : ""} ${
                  codeValue ? "has-value" : ""
                }`}
              />
              {!(phoneVerifyFocus || codeValue) && (
                <span className="blinking-cursor-gold-color verificationCursor">
                  |
                </span>
              )}
            </div>
          </div>
          {loading && <Loader className="mobileVerifySignup-loader-position" />}
          {/* we need this class signup-btn-wrapper to position the svg inside the button */}
          <div className="signup-btn-wrapper confirm-btn-wrapper position-relative">
            {/* Sign Up button component */}
            <ImgTopBtn
              image={
                <BlinkingLight
                  blinkerLightStyles={{
                    marginRight: "10px", // Or 'left: "10px"' for left positioning
                  }}
                />
              }
              imgTopBtnWidth="100%"
              imgTopBtnTextFontSize="1.3rem"
              className="responsiveBtnText"
              imgTopBtnFontColor="#b03a2e"
              imgTopBtnText="Confirm"
              textClassName="responsiveBtnText"
              onClick={handleConfirm}
            />
          </div>
          {otpError && (
            <ErrorMessageModal
              message={otpError}
              onClose={() => setOtpError("")}
            />
          )}
          <div className="link-flex">
            <SmallPlainInsetBtnEventListener
              text=" X Cancel"
              onClick={handleCancel}
            />
            {!skipCondition && ( // do not show during the skip condition
              <SmallPlainInsetBtnEventListener
                text="Request New Code"
                type="button"
                onClick={handleRequestNewCode}
                disabled={isButtonDisabled}
              />
            )}
            {skipCondition && ( // ← only show once timer has hit 0 and the currentContextType data validation allows. Not during reset password and security flows
              <SmallPlainInsetBtnEventListener
                text="Skip >"
                type="button"
                onClick={onSkipVerification}
              />
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
export default OtpPhoneVerificationForm;
